import React, { useState } from 'react';
import UploadImageApi from '../../../../api/ImageUpload.api';

export default function MediaUploadField({
  label,
  fieldName,
  value,
  onInputChange,
  isGallery = false,
  removeFromList = null,
}) {
  // State for loading and error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle drag over (to prevent the default behavior of the browser)
  const handleDragOver = (e) => {
    e.preventDefault(); // Prevent the default behavior
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();

    // Check for dropped files or selected files
    const file = e.target.files
      ? e.target.files[0]
      : e.dataTransfer
        ? e.dataTransfer.files[0]
        : null;

    if (file) {
      const maxSize = 10 * 1024 * 1024; // 10 MB
      if (file.size > maxSize) {
        setError('File size exceeds 10 MB. Please choose a smaller file.');
        return;
      }

      // Determine media type based on file MIME type
      let mediaType;
      if (file.type.startsWith('image/')) {
        mediaType = 'image';
      } else if (file.type.startsWith('video/')) {
        mediaType = 'video';
      } else {
        setError('Invalid file type. Please upload an image or a video.');
        return;
      }

      const formData = new FormData();
      formData.append(mediaType, file);

      setIsLoading(true);
      setError(null); // Reset error

      try {
        // Call UploadImageApi with the correct media type
        const res = await UploadImageApi(formData, mediaType);

        // Handle success response
        onInputChange({
          target: {
            name: fieldName,
            value: res.data.imageUrl || res.data.videoUrl,
          },
        });

        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setError('Upload failed. Please try again.');
        setIsLoading(false);
      }
    }
  };

  const getFileType = (url) => {
    if (!url) return '';
    const extension = url.split('.').pop().toLowerCase();
    const imageExtensions = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'bmp',
      'tiff',
      'webp',
      'svg',
      'heif',
      'heic',
      'ico',
      'raw',
      'apng',
      'jfif',
      'exif',
      'indd',
      'flv',
    ];

    const videoExtensions = [
      'mp4',
      'webm',
      'ogg',
      'avi',
      'mov',
      'flv',
      'wmv',
      'mkv',
      'mpeg',
      'mpg',
      '3gp',
      'rm',
      'rmvb',
      'vob',
      'ts',
      'asf',
      'divx',
      'm4v',
    ];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    return '';
  };

  const fileType = getFileType(value);

  return (
    <div
      className="col-lg-6 col-12"
      onDragOver={handleDragOver} // Enable dragging over the card
      onDrop={handleFileUpload} // Handle drop on the card
    >
      <div className="card my-2 position-relative">
        {/* Overlay to block the UI during upload */}
        {isLoading && (
          <div className="overlay">
            <div className="spinner-border text-white" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        <div className="card-header p-0 custom_image_card">
          {/* Show uploaded image with blackout during upload */}
          {fileType === 'image' && value && (
            <img src={value} alt="Uploaded" className="card-img-top" />
          )}
          {fileType === 'video' && value && (
            <video className="card-img-top" controls>
              <source src={value} />
              Your browser does not support the video tag.
            </video>
          )}
          {isLoading && <div className="image-blackout" />}
        </div>

        <div className="card-body" style={{ minHeight: '130px' }}>
          <div className="form-label">{label}</div>

          {/* Input or gallery view */}
          {
            <input
              className="form-control my-3"
              type="file"
              accept="image/*, video/*"
              onChange={handleFileUpload}
              placeholder="Upload Image or Video"
              disabled={isLoading} // Disable input when uploading
            />
          }
          <small className="text-muted">Max file size: 10 MB</small>
          {removeFromList && (
            <div className="d-flex justify-content-center w-100">
              <button
                type="button"
                className="btn btn-danger mt-2 remove_button_height"
                onClick={removeFromList}
              >
                Remove Image
              </button>
            </div>
          )}
          {/* )} */}

          {/* Show error message if upload failed */}
          {error && (
            <div className="text-danger mt-2">
              <small>{error}</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
